/**
 * STS Popup Manager
 * 
 * Handles the display of popups based on various trigger conditions.
 */

export default class PopupManager {
    constructor() {
        this.popups = [];
        this.initialized = false;
        this.pageViews = 0;
        this.sessionCount = 0;
        this.displayCounts = {};
        this.init();
    }

    /**
     * Initialize the popup manager
     */
    init() {
        if (this.initialized || typeof stsPopups === 'undefined') {
            return;
        }

        this.initialized = true;
        this.popups = stsPopups || [];
        
        this.initCookies();
        
        this.popups.forEach(popup => {
            this.processPopup(popup);
        });
    }

    /**
     * Initialize cookies and session data
     */
    initCookies() {
        this.pageViews = this.getCookie('sts_popup_page_views') || 0;
        this.pageViews = parseInt(this.pageViews) + 1;
        this.setCookie('sts_popup_page_views', this.pageViews, 365);

        if (!sessionStorage.getItem('sts_popup_session_started')) {
            sessionStorage.setItem('sts_popup_session_started', 'true');
            this.sessionCount = this.getCookie('sts_popup_session_count') || 0;
            this.sessionCount = parseInt(this.sessionCount) + 1;
            this.setCookie('sts_popup_session_count', this.sessionCount, 365);
        } else {
            this.sessionCount = this.getCookie('sts_popup_session_count') || 1;
        }

        const displayCountsStr = this.getCookie('sts_popup_display_counts');
        if (displayCountsStr) {
            try {
                this.displayCounts = JSON.parse(displayCountsStr);
            } catch (e) {
                this.displayCounts = {};
            }
        }
    }

    /**
     * Process a popup and set up its triggers
     * 
     * @param {Object} popup Popup configuration
     */
    processPopup(popup) {
        if (!this.shouldDisplayPopup(popup)) {
            return;
        }

        switch (popup.triggerType) {
            case 'time':
                this.setupTimeDelay(popup);
                break;
            case 'scroll_percentage':
                this.setupScrollPercentage(popup);
                break;
            case 'scroll_element':
                this.setupScrollToElement(popup);
                break;
            case 'scroll_pixels':
                this.setupScrollPixels(popup);
                break;
            case 'click':
                this.setupClickTrigger(popup);
                break;
            case 'inactivity':
                this.setupInactivityTrigger(popup);
                break;
            case 'exit_intent':
                this.setupExitIntentTrigger(popup);
                break;
        }

        this.setupCloseButton(popup);
    }

    /**
     * Check if popup should be displayed based on advanced conditions
     * 
     * @param {Object} popup Popup configuration
     * @returns {boolean} Whether popup should be displayed
     */
    shouldDisplayPopup(popup) {
        if (popup.pageViews > 0 && this.pageViews < popup.pageViews) {
            return false;
        }

        if (popup.sessions > 0 && this.sessionCount < popup.sessions) {
            return false;
        }

        if (popup.maxDisplays > 0) {
            const displayCount = this.getDisplayCount(popup.id);
            if (displayCount >= popup.maxDisplays) {
                return false;
            }
        }

        if (!this.checkDisplayFrequency(popup)) {
            return false;
        }

        if (popup.referrerUrl && !this.checkReferrer(popup)) {
            return false;
        }

        // Check device type
        if (!this.checkDeviceType(popup)) {
            return false;
        }

        return true;
    }

    /**
     * Check if popup should be displayed based on frequency settings
     * 
     * @param {Object} popup Popup configuration
     * @returns {boolean} Whether popup should be displayed based on frequency
     */
    checkDisplayFrequency(popup) {
        const popupId = popup.id;
        const frequency = popup.displayFrequency;

        if (frequency === 'once_closed' && this.getCookie(`sts_popup_closed_${popupId}`)) {
            return false;
        }

        if (frequency === 'once_converted' && this.getCookie(`sts_popup_converted_${popupId}`)) {
            return false;
        }

        if (frequency === 'daily') {
            const lastShownDate = this.getCookie(`sts_popup_last_shown_date_${popupId}`);
            const today = new Date().toDateString();
            
            if (lastShownDate === today) {
                const dailyCount = parseInt(this.getCookie(`sts_popup_daily_count_${popupId}`) || 0);
                if (dailyCount >= popup.maxDisplays && popup.maxDisplays > 0) {
                    return false;
                }
            } else {
                this.setCookie(`sts_popup_daily_count_${popupId}`, 0, 365);
                this.setCookie(`sts_popup_last_shown_date_${popupId}`, today, 365);
            }
        }

        if (frequency === 'session') {
            const sessionDisplayed = sessionStorage.getItem(`sts_popup_displayed_${popupId}`);
            if (sessionDisplayed === 'true') {
                return false;
            }
        }

        return true;
    }

    /**
     * Check if popup should be displayed based on referrer
     * 
     * @param {Object} popup Popup configuration
     * @returns {boolean} Whether popup should be displayed based on referrer
     */
    checkReferrer(popup) {
        if (!popup.referrerUrl) {
            return true;
        }

        const referrer = document.referrer;
        const isMatch = referrer.includes(popup.referrerUrl);
        
        return (popup.referrerType === 'show') ? isMatch : !isMatch;
    }

    /**
     * Check if popup should be displayed based on device type
     * 
     * @param {Object} popup Popup configuration
     * @returns {boolean} Whether popup should be displayed based on device type
     */
    checkDeviceType(popup) {
        const deviceTypes = popup.deviceType || ['desktop', 'tablet', 'mobile'];
        
        if (!deviceTypes.length) {
            return true;
        }

        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isTablet = /iPad|tablet|Tablet/i.test(navigator.userAgent);
        
        if (isTablet && deviceTypes.includes('tablet')) {
            return true;
        } else if (isMobile && !isTablet && deviceTypes.includes('mobile')) {
            return true;
        } else if (!isMobile && !isTablet && deviceTypes.includes('desktop')) {
            return true;
        }
        
        return false;
    }

    /**
     * Set up time delay trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupTimeDelay(popup) {
        setTimeout(() => {
            this.showPopup(popup);
        }, popup.delayTime * 1000);
    }

    /**
     * Set up scroll percentage trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupScrollPercentage(popup) {
        jQuery(window).on('scroll', () => {
            const scrollTop = jQuery(window).scrollTop();
            const docHeight = jQuery(document).height();
            const winHeight = jQuery(window).height();
            const scrollPercent = (scrollTop / (docHeight - winHeight)) * 100;
            
            if (scrollPercent >= popup.scrollPercentage) {
                this.showPopup(popup);
                jQuery(window).off('scroll');
            }
        });
    }

    /**
     * Set up scroll to element trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupScrollToElement(popup) {
        if (!popup.scrollElement) {
            return;
        }

        const $element = jQuery(popup.scrollElement);
        if (!$element.length) {
            return;
        }

        jQuery(window).on('scroll', () => {
            const scrollTop = jQuery(window).scrollTop();
            const elementTop = $element.offset().top;
            const windowHeight = jQuery(window).height();
            
            if (scrollTop + windowHeight >= elementTop) {
                this.showPopup(popup);
                jQuery(window).off('scroll');
            }
        });
    }

    /**
     * Set up scroll pixels trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupScrollPixels(popup) {
        jQuery(window).on('scroll', () => {
            const scrollTop = jQuery(window).scrollTop();
            
            if (scrollTop >= popup.scrollPixels) {
                this.showPopup(popup);
                jQuery(window).off('scroll');
            }
        });
    }

    /**
     * Set up click trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupClickTrigger(popup) {
        if (!popup.clickElement) {
            return;
        }

        jQuery(document).on('click', popup.clickElement, (e) => {
            e.preventDefault();
            this.showPopup(popup);
        });
    }

    /**
     * Set up inactivity trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupInactivityTrigger(popup) {
        let inactivityTimer;
        const resetTimer = () => {
            clearTimeout(inactivityTimer);
            inactivityTimer = setTimeout(() => {
                this.showPopup(popup);
            }, popup.inactivityTime * 1000);
        };

        // Reset timer on user activity
        jQuery(document).on('mousemove keypress scroll click', resetTimer);
        resetTimer();
    }

    /**
     * Set up exit intent trigger
     * 
     * @param {Object} popup Popup configuration
     */
    setupExitIntentTrigger(popup) {
        jQuery(document).on('mouseleave', (e) => {
            if (e.clientY < 0) {
                this.showPopup(popup);
            }
        });
    }

    /**
     * Show the popup
     * 
     * @param {Object} popup Popup configuration
     */
    showPopup(popup) {
        const popupId = popup.id;
        const $popup = jQuery(`#sts-popup-${popupId}`);
        
        if (!$popup.length || $popup.is(':visible')) {
            return;
        }

        this.incrementDisplayCount(popupId);
        
        if (popup.displayFrequency === 'session') {
            sessionStorage.setItem(`sts_popup_displayed_${popupId}`, 'true');
        }
        
        if (popup.displayFrequency === 'daily') {
            const dailyCount = parseInt(this.getCookie(`sts_popup_daily_count_${popupId}`) || 0) + 1;
            this.setCookie(`sts_popup_daily_count_${popupId}`, dailyCount, 365);
            this.setCookie(`sts_popup_last_shown_date_${popupId}`, new Date().toDateString(), 365);
        }

        $popup.fadeIn(300);
        jQuery('html').css('overflow-y', 'hidden');
    }

    /**
     * Set up close button
     * 
     * @param {Object} popup Popup configuration
     */
    setupCloseButton(popup) {
        const popupId = popup.id;
        const $popup = jQuery(`#sts-popup-${popupId}`);
        const $closeBtn = $popup.find('.sts-popup-close');
        
        $closeBtn.on('click', (e) => {
            e.preventDefault();
            this.closePopup(popup);
            
            if (popup.displayFrequency === 'once_closed') {
                this.setCookie(`sts_popup_closed_${popupId}`, 'true', 365);
            }
        });

        $popup.find('.sts-popup-overlay').on('click', (e) => {
            if (e.target === e.currentTarget) {
                this.closePopup(popup);
            }
        });

        $popup.find('form').on('submit', () => {
            if (popup.displayFrequency === 'once_converted') {
                this.setCookie(`sts_popup_converted_${popupId}`, 'true', 365);
            }
        });
    }

    /**
     * Close the popup
     * 
     * @param {Object} popup Popup configuration
     */
    closePopup(popup) {
        const $popup = jQuery(`#sts-popup-${popup.id}`);
        $popup.fadeOut(300);
        jQuery('html').css('overflow-y', 'auto');
    }

    /**
     * Get display count for a popup
     * 
     * @param {number} popupId Popup ID
     * @returns {number} Display count
     */
    getDisplayCount(popupId) {
        return this.displayCounts[popupId] || 0;
    }

    /**
     * Increment display count for a popup
     * 
     * @param {number} popupId Popup ID
     */
    incrementDisplayCount(popupId) {
        this.displayCounts[popupId] = (this.displayCounts[popupId] || 0) + 1;
        this.setCookie('sts_popup_display_counts', JSON.stringify(this.displayCounts), 365);
    }

    /**
     * Get cookie value
     * 
     * @param {string} name Cookie name
     * @returns {string|null} Cookie value
     */
    getCookie(name) {
        const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
        return match ? decodeURIComponent(match[3]) : null;
    }

    /**
     * Set cookie
     * 
     * @param {string} name Cookie name
     * @param {string} value Cookie value
     * @param {number} days Cookie expiration in days
     */
    setCookie(name, value, days) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/';
    }
}

jQuery(document).ready(() => {
    new PopupManager();
});
